import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTimeoutFn, useUnmount } from "react-use";
import styled from "styled-components";
import { Box } from "./Box";

interface CursorHolderProps {
  visible?: boolean;
  spring?: boolean;
}

const InitialPosition = styled(motion(Box))`
  position: absolute;
  z-index: 20;
  pointer-events: none;
`;

const Position = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
`;

const PositionCorrector = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform: translate(-50%, -50%);
`;

export const CursorPosition: React.FC<CursorHolderProps> = ({
  visible,
  spring = false,
  children,
}) => {
  const [init, setInit] = useState(false);
  const [{ xI, yI }, setPosition] = useState({ xI: 0, yI: 0 });

  const timer = useRef<NodeJS.Timeout | null>(null);

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const onMouseMove = (e: MouseEvent) => {
    if (xI === 0 && yI === 0) {
      setPosition({ xI: e.clientX, yI: e.clientY });
      return;
    }
    x.set(e.clientX - xI);
    y.set(e.clientY - yI);
  };

  const physics = { damping: 2, mass: 0.02, stiffness: true ? 20 : 300 }; // easing of smooth scroll
  const xSpring = useSpring(x, physics); // apply easing to the negative scroll value
  const ySpring = useSpring(y, physics); // apply easing to the negative scroll value

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove, false);

    return () => {
      window.removeEventListener("mousemove", onMouseMove, false);
    };
  });

  return (
    <InitialPosition top={yI} left={xI}>
      <Position
        id="cursor"
        style={!spring ? { x, y } : { x: xSpring, y: ySpring }}
      >
        <PositionCorrector>{children}</PositionCorrector>
      </Position>
    </InitialPosition>
  );
};
